import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Clock, Sparkles, ChevronRight, Compass,
} from "lucide-react";

export default function PathwayPage() {
  const { pathwayId } = useParams();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [{ data: c }, { data: p }] = await Promise.all([
        api.get(`/courses/${pathwayId}`),
        api.get("/courses/progress"),
      ]);
      setCourse(c.course);
      setProgress(p);
      setEnrollment((p.enrollments || []).find((e) => e.path_id === pathwayId) || null);
    } catch (err) {
      setError(extractApiError(err, "Failed to load pathway."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [pathwayId]);

  const enroll = async () => {
    setEnrolling(true);
    try {
      const active = (progress?.enrollments || []).find((e) => e.active);
      if (active && active.path_id !== pathwayId) {
        await api.post("/courses/enrollments/switch", { path_id: pathwayId });
      } else {
        await api.post("/courses/enrollments", { path_id: pathwayId, confirmed: true });
      }
      await load();
      toast.success("Enrolled — you can start any lesson below.");
    } catch (err) {
      toast.error(extractApiError(err, "Enroll failed."));
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (<AppShell><div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="pathway-loading">Loading…</div></AppShell>);
  }
  if (error || !course) {
    return (<AppShell><div className="mx-auto max-w-3xl px-4 py-14"><Alert variant="destructive"><AlertDescription data-testid="pathway-error">{error || "Not found."}</AlertDescription></Alert></div></AppShell>);
  }

  const doneSet = new Set((progress?.lesson_progress || []).filter((p) => p.status === "completed").map((p) => p.module_id));
  const isActive = enrollment?.active;

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/learning" className="text-xs text-fg-secondary hover:text-fg-primary transition-colors" data-testid="pathway-back-link">
          <ArrowLeft className="h-3 w-3 inline mr-1" aria-hidden="true" />
          Back to learning
        </Link>
        <header className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-2">
            <Compass className="h-3 w-3" aria-hidden="true" />
            Pathway
          </p>
          <h1 className="mt-1 text-3xl font-heading font-medium text-fg-primary" data-testid="pathway-title">{course.title}</h1>
          <p className="mt-3 text-sm text-fg-secondary">{course.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {!isActive ? (
              <Button
                onClick={enroll} disabled={enrolling}
                data-testid="pathway-enroll-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                {enrolling ? "Enrolling…" : "Enroll in this pathway"}
              </Button>
            ) : (
              <p data-testid="pathway-enrolled-note" className="text-xs text-progress-teal">You're enrolled in this pathway.</p>
            )}
          </div>
        </header>

        <section className="mt-8" data-testid="pathway-lessons">
          <h2 className="text-xl font-heading font-medium text-fg-primary">Lessons</h2>
          <div className="mt-3 space-y-2">
            {course.modules.map((m) => {
              const done = doneSet.has(m.id);
              return (
                <Link
                  key={m.id} to={`/learning/module/${m.id}`}
                  data-testid={`pathway-lesson-${m.id}`}
                  className="flex items-center justify-between rounded-lg border border-border-subtle bg-bg-surface p-4 hover:border-accent-warm/40 transition-colors"
                >
                  <div>
                    <p className="text-xs text-fg-secondary">
                      {m.reading_time_min ? `${m.reading_time_min} min · ` : ""}
                      {done ? <span className="text-progress-teal">Completed</span> : <span className="text-fg-muted">Not yet complete</span>}
                    </p>
                    <p className="mt-1 text-sm text-fg-primary font-medium">{m.order}. {m.title}</p>
                  </div>
                  {done ? <CheckCircle2 className="h-4 w-4 text-progress-teal" aria-hidden="true" /> : <ChevronRight className="h-4 w-4 text-fg-muted" aria-hidden="true" />}
                </Link>
              );
            })}
          </div>
        </section>

        {course.capstone && (
          <section className="mt-8" data-testid="pathway-capstone-section">
            <h2 className="text-xl font-heading font-medium text-fg-primary flex items-center gap-2">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Capstone
            </h2>
            <div className="mt-3 rounded-lg border border-border-subtle bg-bg-surface p-5">
              <p className="text-sm text-fg-primary font-medium">{course.capstone.title}</p>
              <p className="mt-2 text-sm text-fg-secondary">{course.capstone.prompt}</p>
              <Link to={`/learning/project/${course.capstone.id}`}>
                <Button size="sm" className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover" data-testid="pathway-open-capstone-btn">
                  Open the capstone
                </Button>
              </Link>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
